import { api } from '../apiClient';
import { AppInfo, AppListResponse, AppCategory } from '@/types/app';

export const servicesAPI = {
  getUserServices: () => {
    return api.get('/services/users/services').then(response => response.data);
  },
  
  getServiceToken: (serviceSlug: string) => {
    return api.get(`/services/${serviceSlug}/token`).then(response => response.data);
  },

  redirectToService: async (serviceSlug: string) => {
    // 백엔드에서 redirect_url을 반환하고, 프론트엔드에서 새 탭으로 열기
    try {
      const response = await api.get(`/services/${serviceSlug}/redirect`);
      const redirectUrl = response.data?.redirect_url;
      
      if (redirectUrl) {
        // 새 탭에서 열기 (팝업 차단 방지를 위해 클릭 핸들러에서 직접 호출해야 함)
        // 여기서는 URL만 반환하고, 실제 window.open은 클릭 핸들러에서 호출
        return { data: { redirect_url: redirectUrl } };
      }
      
      throw new Error('리다이렉트 URL을 받지 못했습니다.');
    } catch (error: any) {
      // 에러 정보를 구조화하여 throw
      const status = error.response?.status;
      let detail = '알 수 없는 오류가 발생했습니다.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          detail = error.response.data.detail || error.response.data.message || detail;
        } else if (typeof error.response.data === 'string') {
          try {
            const parsed = JSON.parse(error.response.data);
            detail = parsed.detail || parsed.message || detail;
          } catch {
            detail = error.response.data;
          }
        }
      } else if (error.message) {
        detail = error.message;
      }
      
      const structuredError: any = new Error(detail);
      structuredError.response = {
        status: status || 0,
        data: {
          detail: detail,
        },
      };
      structuredError.status = status;
      structuredError.detail = detail;
      
      throw structuredError;
    }
  },
  
  searchPublicServices: (query?: string, category?: string) => {
    const params: any = {};
    if (query) params.query = query;
    if (category) params.category = category;
    return api.get('/services/public/search', { params }).then(response => response.data);
  },
  
  subscribeToService: (serviceSlug: string) => {
    return api.post(`/services/${serviceSlug}/subscribe`).then(response => response.data);
  },

  unsubscribeFromService: (serviceSlug: string) => {
    return api.delete(`/services/${serviceSlug}/subscribe`).then(response => response.data);
  },
  
  // 결제 API
  initiatePayment: (paymentData: {
    type: 'credit' | 'subscription';
    amount?: number;
    credits?: number;
    subscription_id?: number;
    customer_id?: number;
  }) => {
    return api.post('/payment/initiate', paymentData).then(response => response.data);
  },
  
  confirmPayment: (paymentId: string, paymentKey: string) => {
    return api.post('/payment/confirm', { payment_id: paymentId, payment_key: paymentKey }).then(response => response.data);
  },

  // ==================== Workflow Builder - App APIs ====================

  /**
   * 워크플로우 빌더에서 사용 가능한 앱 목록 조회
   * - 공개 앱 + 권한 있는 비공개 앱
   */
  getAvailableApps: async (params?: {
    category?: AppCategory;
    search?: string;
  }): Promise<AppListResponse> => {
    const response = await api.get<AppListResponse>('/services/workflow-apps', { params });
    return response.data;
  },

  /**
   * 특정 앱의 상세 정보 조회 (스키마 포함)
   */
  getAppDetails: async (serviceSlug: string): Promise<AppInfo> => {
    const response = await api.get<AppInfo>(`/services/${serviceSlug}/details`);
    return response.data;
  },

  /**
   * 앱의 입력 스키마 조회 (워크플로우 설정용)
   */
  getAppInputSchema: async (serviceSlug: string): Promise<Record<string, any>> => {
    const response = await api.get(`/services/${serviceSlug}/input-schema`);
    return response.data;
  },

  /**
   * 앱의 출력 스키마 조회 (입력 매핑용)
   */
  getAppOutputSchema: async (serviceSlug: string): Promise<Record<string, any>> => {
    const response = await api.get(`/services/${serviceSlug}/output-schema`);
    return response.data;
  },
};

